'use client';

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useGlobal } from '@/contexts/GlobalContext';
import { useModal } from '@/contexts/ModalContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import { useToast } from '@/contexts/ToastContext';
import LoadingModal from '@/components/common/LoadingModal';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import TeamProfileWithJoinButton from '@/components/teams/TeamProfileWithJoinButton';
import Button from '@/components/common/Button';
import TeamLabel from '@/components/teams/TeamLabel';
import { mutateWithoutLoading } from '@/hooks/swr/useSWRWithLoadingControl';
import { TeamModel } from '@/types/teams';
import { approveInvitation, leaveTeam } from '@/repositories/teams';

interface TeamJoinModalProps {
  propsTeam: TeamModel;
}

const TeamJoinModal: React.FC<TeamJoinModalProps> = ({ propsTeam }) => {
  /**
   * react context
   */
  const context = useGlobal();
  const { showSuccessMessage, showErrorMessage } = useToast();
  const { closeModal } = useModal();
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  /**
   * Next router
   */
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * モーダル閉じる
   */
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // モーダルの背景をクリックした場合にだけ closeModal を呼び出す
    const modalBackground = document.getElementById('modal-background');
    if (e.target === modalBackground) {
      closeModal();
    }
  };

  /**
   * チーム招待の認可（参加する）
   */
  const handleApproveInvitation = async () => {
    // ローディングモーダル表示
    openConfirmModal(<LoadingModal />);

    try {
      // チーム招待の承認
      const res = await approveInvitation(propsTeam.uuid, context.user?.id as number, context.csrftoken || '');
      if (!res.ok) {
        throw new Error(res.detail);
      }

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      // mutateWithoutLoading();
      // グローバルコンテキストの状態を最新に更新
      // 単なる状態更新の実装も試したが、何故かチームページからマイページに戻った際に
      // 古い状態で表示されてしまう。router.refresh()だと余計なクエリを発行してしまうが、
      // 確実に状態が更新されてキャッシュにも残らないので一旦この実装とした。
      // navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Team Home Url
      const teamHomeUrl = `/teams/${propsTeam.uuid}`;

      // NOTE:チーム参加後、チームページ移動(初回メッセージの表示つき)
      navigate(`${teamHomeUrl}?initial-view-message=${propsTeam.name}のメンバーになりました！`, {
        replace: true,
        state: { skipLoading: true }
      });
    } catch (err) {
      // エラートースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      throw err;
    } finally {
      // チーム招待リクエストモーダルを閉じる
      closeModal();
      // ローディングモーダル閉じる
      closeConfirmModal();
    }
  };

  /**
   * チーム招待の取り消し（参加しない）
   */
  const handleCancelInvitation = async () => {
    // 確認モーダル閉じる
    closeConfirmModal();

    // ローディングモーダル表示
    openConfirmModal(<LoadingModal />);

    try {
      // チーム招待の取り消し
      const res = await leaveTeam(propsTeam.uuid, context.user?.id as number, context.csrftoken || '');
      if (!res.ok) {
        throw new Error(res.detail);
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // トースト表示
      showSuccessMessage('リクエストを拒否しました。');
    } catch (err) {
      // エラートースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      throw err;
    } finally {
      // チーム招待リクエストモーダルを閉じる
      closeModal();
      // ローディングモーダル閉じる
      closeConfirmModal();
    }
  };

  /**
   * 確認モーダル表示
   */
  const handleConfirmCancelInvitation = () => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleCancelInvitation}>
        このチームには参加しません。
        <br />
        本当によろしいですか？
        <br />
        <span className="text-xs">※再招待は、チーム管理者に問い合わせください。</span>
      </ConfirmModalContent>
    );
  };

  return (
    <div
      id="modal-background"
      className="fixed inset-0 z-30 w-full flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
      onClick={handleModalClick}>
      <div className="relative w-11/12 sm:w-6/12 max-h-[85dvh] overflow-y-auto rounded-lg">
        <div className="relative flex flex-col bg-white border-0 rounded-lg outline-none focus:outline-none">
          <div className="flex justify-between items-center border-b border-green-500 px-4 py-2 bg-white sticky top-0 z-10 rounded-t-lg">
            <h3 className="flex items-center font-footballlog italic text-sm font-semibold text-gray-500 dark:text-white">
              {propsTeam.name}からチームへの招待が届きました！
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <div className="p-4">
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="mb-2">
                <TeamProfileWithJoinButton propsTeam={propsTeam} />
              </div>
              <div className="mb-8">
                <div className="border border-green-500 p-4 rounded-lg">
                  <ul className="list space-y-4 text-gray-600 text-sm">
                    <li className="flex items-top space-x-3 rtl:space-x-reverse">
                      <svg
                        className="flex-shrink-0 w-3 h-3 mt-1 text-green-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                      <span>
                        参加すると<span className="font-semibold">{propsTeam.name}の専用チームページ</span>
                        にアクセスできます。
                      </span>
                    </li>
                    <li className="flex items-top space-x-3 rtl:space-x-reverse">
                      <svg
                        className="flex-shrink-0 w-3 h-3  mt-1 text-green-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                      <span>
                        チームプランが適用され、<span className="font-semibold">footballLOGの有料機能</span>
                        を利用できます。
                      </span>
                    </li>
                    <li className="flex items-top space-x-3 rtl:space-x-reverse">
                      <svg
                        className="flex-shrink-0 w-3 h-3 mt-1 text-green-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                      <span className="float-left">
                        チームページでは、
                        <TeamLabel
                          propsTeamName={propsTeam.name}
                          propsColorA={propsTeam.color_a}
                          propsColorB={propsTeam.color_b}
                          propsTeamEmblem={propsTeam.team_emblem?.file_path}
                        />
                        &nbsp;アイコン付きの<span className="font-semibold">チームノート</span>
                        を作成できます。
                      </span>
                    </li>
                    <li className="flex items-top space-x-3 rtl:space-x-reverse">
                      <svg
                        className="flex-shrink-0 w-3 h-3 mt-1 text-green-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                      <span>チームノートはチーム全体で共有されるサッカーノートです。</span>
                    </li>
                    <li className="flex items-top space-x-3 rtl:space-x-reverse">
                      <svg
                        className="flex-shrink-0 w-3 h-3 mt-1 text-green-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 16 12">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M1 5.917 5.724 10.5 15 1.5"
                        />
                      </svg>
                      <span className="block whitespace-normal">
                        <span>チームノートもマイページの</span>
                        <svg
                          className="w-4 h-4 inline-block align-middle mx-1"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 20 18">
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M10 16.5c0-1-8-2.7-9-2V1.8c1-1 9 .707 9 1.706M10 16.5V3.506M10 16.5c0-1 8-2.7 9-2V1.8c-1-1-9 .707-9 1.706"
                          />
                        </svg>
                        <span>サッカーノートメニューからいつでも確認、管理ができます。</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="pt-2 pb-8 text-center text-lg font-semibold">
                <p>このチームに参加しますか？</p>
              </div>
              <div className="flex pb-3 justify-center">
                <Button
                  type="button"
                  buttonType="outlineSecondary"
                  className="bg-white"
                  loadingMessage="参加しない"
                  onClick={handleConfirmCancelInvitation}>
                  参加しない
                </Button>
                <Button type="button" buttonType="primary" className="ms-4" onClick={handleApproveInvitation}>
                  参加する
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamJoinModal;
